// Task 32 — per-engine spatial-index DDL preview text. PREVIEW ONLY: `SpatialIndexHint.tsx`
// renders this in a read-only `<pre>` with a Copy button and deliberately has NO run/execute
// action — the user reviews and runs it themselves.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export type SpatialIndexBbox = readonly [number, number, number, number];

/** `schema` may be `''` when unknown (the execution-plan surface can't always recover it from
 *  the plan alone) — in that case the qualifier is simply omitted rather than emitting a bogus
 *  leading dot. The TE trigger always has a real schema, so its scripts stay fully qualified. */
function qualify(schema: string, table: string): string {
  return schema.trim().length > 0 ? `${schema}.${table}` : table;
}

export function buildSpatialIndexScript(
  engine: DatabaseEngineId,
  schema: string,
  table: string,
  column: string,
  bbox?: SpatialIndexBbox
): string {
  switch (engine) {
    case 'pgsql':
      return `CREATE INDEX CONCURRENTLY idx_${table}_${column}_gist ON ${qualify(schema, table)} USING GIST (${column});`;

    case 'mysql':
      return [
        '-- SPATIAL indexes require the column to be NOT NULL and every value to carry an explicit SRID.',
        `ALTER TABLE ${table} ADD SPATIAL INDEX idx_${table}_${column} (${column});`,
      ].join('\n');

    case 'mssql': {
      const boundingBox = bbox
        ? `${bbox[0]}, ${bbox[1]}, ${bbox[2]}, ${bbox[3]}`
        : '-- tune-me: xmin, ymin, xmax, ymax';
      return [
        "-- Adjust BOUNDING_BOX to your data's real extent before running.",
        `CREATE SPATIAL INDEX idx_${table}_${column}`,
        `ON ${qualify(schema, table)}(${column})`,
        `WITH (BOUNDING_BOX = (${boundingBox}));`,
      ].join('\n');
    }

    case 'oracle':
      return [
        '-- tune-me: dimension ranges + SRID for your data',
        'INSERT INTO USER_SDO_GEOM_METADATA (TABLE_NAME, COLUMN_NAME, DIMINFO, SRID)',
        `VALUES ('${table.toUpperCase()}', '${column.toUpperCase()}',`,
        "  SDO_DIM_ARRAY(SDO_DIM_ELEMENT('X', -180, 180, 0.05), SDO_DIM_ELEMENT('Y', -90, 90, 0.05)),",
        '  4326);',
        `CREATE INDEX idx_${table}_${column} ON ${qualify(schema, table)}(${column})`,
        '  INDEXTYPE IS MDSYS.SPATIAL_INDEX_V2;',
      ].join('\n');

    case 'sqlite':
      return `SELECT CreateSpatialIndex('${table}', '${column}');`;

    default:
      return '';
  }
}
